import { motion } from "framer-motion";
import { GraduationCap, Network, Brain, ScanLine, Sparkles, ArrowRight } from "lucide-react";
import { SUBJECTS } from "@/components/Kit";

const HERO = "https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=1600&q=75";
const FEATURES = [
  { icon: Network, title: "Recording → Notes", desc: "Paste a Drive link. Gemini listens and writes exam-ready notes." },
  { icon: Brain, title: "Mindmaps & Boards", desc: "Interactive mindmaps plus your Miro whiteboards in one place." },
  { icon: ScanLine, title: "AI-Graded Exams", desc: "MCQ + written answers graded instantly, results emailed." },
];

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function Login() {
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#090D16] grid lg:grid-cols-2" data-testid="login-page">
      <div className="relative flex flex-col px-6 sm:px-12 lg:px-16 py-10 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[30rem] h-[30rem] rounded-full bg-emerald-500/10 blur-[120px] pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-400 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-slate-950" /></div>
          <span className="font-display font-extrabold text-xl tracking-tight">Vivek<span className="text-emerald-400"> AI</span></span>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative flex-1 flex flex-col justify-center max-w-xl py-12">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400 mb-5 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Grades 7–10 · Powered by Gemini</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-display tracking-tighter leading-[1.05]">
            Every lesson,<br />turned into <span className="text-emerald-400">understanding</span>.
          </h1>
          <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-md">
            Recorded classes become precise notes, mindmaps and AI-graded exams — organised by grade and subject.
          </p>

          <button data-testid="google-login-button" onClick={handleGoogleLogin}
            className="group mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white text-slate-900 font-semibold rounded-full pl-5 pr-6 py-3.5 shadow-xl shadow-emerald-500/10 transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]">
            <GoogleIcon /> Continue with Google <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>

          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.1 }}
                className="rounded-2xl bg-[#111726] border border-[#1E293B] p-4">
                <f.icon className="w-5 h-5 text-emerald-400 mb-3" />
                <div className="text-sm font-semibold text-slate-100">{f.title}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{f.desc}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        <div className="relative text-xs text-slate-600">Super Admin authorizes teachers · Teachers build lessons · Students pick their grade</div>
      </div>

      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Student learning" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090D16] via-[#090D16]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090D16] via-transparent to-transparent" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="absolute bottom-12 left-12 right-12 rounded-3xl bg-[#090D16]/70 backdrop-blur-xl border border-white/10 p-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Subjects</div>
          <div className="flex gap-3">
            {SUBJECTS.map((s) => (
              <div key={s.name} className="flex-1 flex items-center gap-2 rounded-xl px-3 py-2.5" style={{ background: `${s.color}14`, border: `1px solid ${s.color}40` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} /><span className="text-sm font-semibold text-slate-100">{s.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
